import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Menu, X, Globe, MessageCircle, User, LogOut, ShieldCheck } from "lucide-react";

import { waChatLink } from "@/lib/whatsapp";

export const Navbar = () => {
  const { t, lang, toggleLang, currency, toggleCurrency, user, logout } = useApp();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const nav = useNavigate();

  const links = [
    { to: "/flights", label: t.nav.flights },
    { to: "/hotels", label: t.nav.hotels },
    { to: "/services?type=visa", label: t.nav.visas },
    { to: "/packages", label: t.nav.groups },
    { to: "/tips", label: t.nav.tips },
    { to: "/about", label: t.nav.about },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header className="sticky top-0 z-50 glass border-b border-slate-200/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-20 sm:h-24">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 shrink-0">
          <img src="/logo.png?v=3" alt={t.brand} className="h-16 sm:h-[84px] w-auto object-contain" data-testid="brand-logo-img" />
          <span className="hidden md:block font-display text-lg font-bold text-tigris leading-tight" data-testid="brand-name">{t.brand}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.to}`}
              className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-tigris rounded-lg hover:bg-tigris/5 transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button onClick={toggleCurrency} data-testid="currency-toggle"
            className="hidden sm:inline-flex items-center px-2.5 py-1.5 text-xs font-bold text-tigris border border-tigris/30 rounded-lg hover:bg-tigris/5 transition-colors">
            {currency === "USD" ? "USD $" : "IQD د.ع"}
          </button>
          <button onClick={toggleLang} data-testid="lang-toggle"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-tigris border border-tigris/30 rounded-lg hover:bg-tigris/5 transition-colors">
            <Globe size={14} /> {lang === "ar" ? "EN" : "ع"}
          </button>
          <a href={waChatLink(t.common.waGreeting)} target="_blank" rel="noreferrer" data-testid="navbar-whatsapp"
            className="btn-wa hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-lg">
            <MessageCircle size={18} />
          </a>

          {user ? (
            <div className="relative">
              <button onClick={() => setMenu((m) => !m)} data-testid="user-menu-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 btn-primary rounded-lg text-sm font-semibold">
                <User size={16} /> <span className="hidden sm:inline max-w-[90px] truncate">{user.name || t.auth.account}</span>
              </button>
              {menu && (
                <div className="absolute end-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 overflow-hidden">
                  <button onClick={() => { setMenu(false); nav("/dashboard"); }} data-testid="menu-bookings"
                    className="w-full text-start px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2">
                    <User size={15} /> {t.auth.myBookings}
                  </button>
                  {(user.role === "admin" || user.role === "staff") && (
                    <button onClick={() => { setMenu(false); nav("/admin"); }} data-testid="menu-admin"
                      className="w-full text-start px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2 text-gold font-semibold">
                      <ShieldCheck size={15} /> {t.admin.title}
                    </button>
                  )}
                  <button onClick={() => { setMenu(false); logout(); nav("/"); }} data-testid="menu-logout"
                    className="w-full text-start px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2 text-red-600">
                    <LogOut size={15} /> {t.auth.logout}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/auth" data-testid="login-nav-btn"
              className="btn-primary inline-flex items-center px-3.5 py-2 rounded-lg text-sm font-semibold">
              {t.auth.login}
            </Link>
          )}

          <button onClick={() => setOpen((o) => !o)} className="lg:hidden p-2 text-tigris" data-testid="mobile-menu-btn">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200/60 bg-white px-4 py-3 space-y-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-tigris/5 rounded-lg">
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};
